import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { StepperService } from '../../../../services/stepper.service';
import { CityResponse, UniversityResponse } from '../../../initial-form/initialForm.models';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-colombia-selector',
  templateUrl: './colombia-selector.component.html',
  styleUrls: ['./colombia-selector.component.scss']
})
export class ColombiaSelectorComponent implements OnInit {
  @Input() form!: FormGroup;

  allCities: CityResponse[] = [];
  allUniversities: UniversityResponse[] = [];

  cityOptions: { value: string; label: string }[] = [];
  universityMainOptions: { value: string; label: string }[] = [];
  universityExtraOptions: { value: string; label: string }[] = [];

  isLoadingCities = true;
  isLoadingUniversities = true;

  constructor(private stepperService: StepperService) {}

  ngOnInit(): void {
    this.loadData().then(() => this.listenToCityAndUniversityChanges());
  }

  private loadData(): Promise<void> {
    return new Promise((resolve, reject) => {
      forkJoin({
        cities: this.stepperService.loadColombianCities(),
        universities: this.stepperService.laodColombianUniversities()
      }).subscribe(
        ({ cities, universities }) => {
          this.allCities = cities;
          this.allUniversities = universities;
          this.cityOptions = this.mapToCityOptions(cities);
          this.updateUniversityOptions();
          this.isLoadingCities = false;
          this.isLoadingUniversities = false;
          resolve();
        },
        (error) => {
          console.error('Error loading cities or universities', error);
          reject(error);
        }
      );
    });
  }

  private listenToCityAndUniversityChanges(): void {
    this.form.get('primaryCity')?.valueChanges.subscribe(() => this.updateUniversityOptions());
    this.form.get('secondaryCities')?.valueChanges.subscribe(() => this.updateUniversityOptions());
    this.form.get('mainColombianUniversity')?.valueChanges.subscribe(() => this.updateUniversityOptions());

    this.form.get('colombianUniversities')?.valueChanges.subscribe(() => {
      this.syncExtraUniversitiesWithMain();
    });
  }

  private updateUniversityOptions(): void {
    const selectedCities = this.getSelectedCities();

    const filteredUniversities = this.allUniversities.filter(
      university =>
        !university.city || selectedCities.includes(university.city.nombre)
    );

    const selectedIds = [
      this.form.get('mainColombianUniversity')?.value,
      ...(this.form.get('colombianUniversities')?.value || [])
    ];

    const selectedUniversities = this.allUniversities.filter(u => selectedIds.includes(u._id));
    const merged = new Map<string, UniversityResponse>();

    [...filteredUniversities, ...selectedUniversities].forEach(u => {
      if (u) merged.set(u._id, u);
    });

    const mapped = this.mapToUniversityOptions(Array.from(merged.values()));
    const mainUniversity = this.form.get('mainColombianUniversity')?.value;

    this.universityMainOptions = mapped;
    this.universityExtraOptions = mapped.filter(u => u.value !== mainUniversity);

    this.syncExtraUniversitiesWithMain();
  }

  private syncExtraUniversitiesWithMain(): void {
    const mainUniversity = this.form.get('mainColombianUniversity')?.value;
    const extraUniversitiesCtrl = this.form.get('colombianUniversities') as FormControl;

    if (!extraUniversitiesCtrl || !Array.isArray(extraUniversitiesCtrl.value)) return;

    const values: string[] = extraUniversitiesCtrl.value;
    const filtered = values.filter((u) => u !== mainUniversity);

    if (filtered.length !== values.length) {
      extraUniversitiesCtrl.setValue(filtered);
    }
  }

  private getSelectedCities(): string[] {
    const primary = this.form.get('primaryCity')?.value;
    const secondary = this.form.get('secondaryCities')?.value || [];
    return [...new Set([primary, ...secondary].filter(Boolean))];
  }

  private mapToCityOptions(cities: CityResponse[]): { value: string; label: string }[] {
    const unique = new Map<string, CityResponse>();
    cities.forEach(c => {
      if (!unique.has(c.nombre)) unique.set(c.nombre, c);
    });

    return Array.from(unique.values()).map(c => ({ value: c.nombre, label: c.nombre }));
  }

  private mapToUniversityOptions(universities: UniversityResponse[]): { value: string; label: string }[] {
    const unique = new Map<string, UniversityResponse>();
    universities.forEach(u => {
      if (!unique.has(u._id)) unique.set(u._id, u);
    });

    return Array.from(unique.values()).map(u => ({
      value: u._id,
      label: u.city?.nombre ? `${u.name} - ${u.city.nombre}` : u.name,
    }));
  }
}
